import React, { useState, useRef, useEffect } from "react";
import {
  Plane,
  Hotel,
  Ship,
  Package,
  Search,
  Users,
  Star,
  Calendar,
  ArrowRightLeft,
  MapPin,
} from "lucide-react";
import "./SearchCardNew.css";

// Popular Airports Array
const AIRPORTS = [
  { code: "JFK", name: "John F. Kennedy International Airport", city: "New York", country: "USA" },
  { code: "LAX", name: "Los Angeles International Airport", city: "Los Angeles", country: "USA" },
  { code: "LHR", name: "London Heathrow Airport", city: "London", country: "UK" },
  { code: "DXB", name: "Dubai International Airport", city: "Dubai", country: "UAE" },
  { code: "HND", name: "Tokyo Haneda Airport", city: "Tokyo", country: "Japan" },
  { code: "CDG", name: "Charles de Gaulle Airport", city: "Paris", country: "France" },
  { code: "SIN", name: "Singapore Changi Airport", city: "Singapore", country: "Singapore" },
  { code: "SYD", name: "Sydney Kingsford Smith Airport", city: "Sydney", country: "Australia" },
  { code: "FRA", name: "Frankfurt Airport", city: "Frankfurt", country: "Germany" },
  { code: "AMS", name: "Amsterdam Schiphol Airport", city: "Amsterdam", country: "Netherlands" },
  { code: "ORD", name: "O'Hare International Airport", city: "Chicago", country: "USA" },
  { code: "ATL", name: "Hartsfield-Jackson Atlanta International Airport", city: "Atlanta", country: "USA" },
  { code: "DFW", name: "Dallas/Fort Worth International Airport", city: "Dallas", country: "USA" },
  { code: "DEN", name: "Denver International Airport", city: "Denver", country: "USA" },
  { code: "SFO", name: "San Francisco International Airport", city: "San Francisco", country: "USA" },
  { code: "SEA", name: "Seattle-Tacoma International Airport", city: "Seattle", country: "USA" },
  { code: "MIA", name: "Miami International Airport", city: "Miami", country: "USA" },
  { code: "LAS", name: "Harry Reid International Airport", city: "Las Vegas", country: "USA" },
  { code: "BOS", name: "Boston Logan International Airport", city: "Boston", country: "USA" },
  { code: "MCO", name: "Orlando International Airport", city: "Orlando", country: "USA" },
  { code: "YYZ", name: "Toronto Pearson International Airport", city: "Toronto", country: "Canada" },
  { code: "YVR", name: "Vancouver International Airport", city: "Vancouver", country: "Canada" },
  { code: "MEX", name: "Mexico City International Airport", city: "Mexico City", country: "Mexico" },
  { code: "GRU", name: "São Paulo/Guarulhos International Airport", city: "São Paulo", country: "Brazil" },
  { code: "MAD", name: "Adolfo Suárez Madrid-Barajas Airport", city: "Madrid", country: "Spain" },
  { code: "BCN", name: "Barcelona-El Prat Airport", city: "Barcelona", country: "Spain" },
  { code: "FCO", name: "Leonardo da Vinci-Fiumicino Airport", city: "Rome", country: "Italy" },
  { code: "MXP", name: "Milan Malpensa Airport", city: "Milan", country: "Italy" },
  { code: "MUC", name: "Munich Airport", city: "Munich", country: "Germany" },
  { code: "ZRH", name: "Zurich Airport", city: "Zurich", country: "Switzerland" },
  { code: "VIE", name: "Vienna International Airport", city: "Vienna", country: "Austria" },
  { code: "IST", name: "Istanbul Airport", city: "Istanbul", country: "Turkey" },
  { code: "DOH", name: "Hamad International Airport", city: "Doha", country: "Qatar" },
  { code: "AUH", name: "Abu Dhabi International Airport", city: "Abu Dhabi", country: "UAE" },
  { code: "BKK", name: "Suvarnabhumi Airport", city: "Bangkok", country: "Thailand" },
  { code: "KUL", name: "Kuala Lumpur International Airport", city: "Kuala Lumpur", country: "Malaysia" },
  { code: "HKG", name: "Hong Kong International Airport", city: "Hong Kong", country: "Hong Kong" },
  { code: "ICN", name: "Incheon International Airport", city: "Seoul", country: "South Korea" },
  { code: "NRT", name: "Narita International Airport", city: "Tokyo", country: "Japan" },
  { code: "PEK", name: "Beijing Capital International Airport", city: "Beijing", country: "China" },
  { code: "PVG", name: "Shanghai Pudong International Airport", city: "Shanghai", country: "China" },
  { code: "DEL", name: "Indira Gandhi International Airport", city: "Delhi", country: "India" },
  { code: "BOM", name: "Chhatrapati Shivaji Maharaj International Airport", city: "Mumbai", country: "India" },
  { code: "BLR", name: "Kempegowda International Airport", city: "Bangalore", country: "India" },
  { code: "JNB", name: "O.R. Tambo International Airport", city: "Johannesburg", country: "South Africa" },
  { code: "CAI", name: "Cairo International Airport", city: "Cairo", country: "Egypt" },
  { code: "CPT", name: "Cape Town International Airport", city: "Cape Town", country: "South Africa" },
  { code: "MEL", name: "Melbourne Airport", city: "Melbourne", country: "Australia" },
  { code: "BNE", name: "Brisbane Airport", city: "Brisbane", country: "Australia" },
  { code: "AKL", name: "Auckland Airport", city: "Auckland", country: "New Zealand" },
];

function SearchCardNew({ onSearch, initialData }) {
  const [showPassengerDropdown, setShowPassengerDropdown] = useState(false);
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [fromSearchQuery, setFromSearchQuery] = useState("");
  const [toSearchQuery, setToSearchQuery] = useState("");

  const departDateRef = useRef(null);
  const returnDateRef = useRef(null);
  const passengerDropdownRef = useRef(null);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);

  const [searchData, setSearchData] = useState({
    from: initialData?.from || "",
    to: initialData?.to || "",
    fromDisplay: initialData?.fromDisplay || "",
    toDisplay: initialData?.toDisplay || "",
    departDate: initialData?.departDate || "",
    returnDate: initialData?.returnDate || "",
    tripType: initialData?.tripType || "round-trip",
    passengers: initialData?.passengers || {
      adults: 1,
      children: 0,
      infants: 0,
    },
    cabinClass: initialData?.cabinClass || "economy",
  });

  // Update searchData when initialData changes
  useEffect(() => {
    if (initialData) {
      setSearchData({
        from: initialData.from || "",
        to: initialData.to || "",
        fromDisplay: initialData.fromDisplay || "",
        toDisplay: initialData.toDisplay || "",
        departDate: initialData.departDate || "",
        returnDate: initialData.returnDate || "",
        tripType: initialData.tripType || "round-trip",
        passengers: initialData.passengers || {
          adults: 1,
          children: 0,
          infants: 0,
        },
        cabinClass: initialData.cabinClass || "economy",
      });
    }
  }, [initialData]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target)) {
        setShowFromDropdown(false);
      }
      if (toDropdownRef.current && !toDropdownRef.current.contains(event.target)) {
        setShowToDropdown(false);
      }
      if (passengerDropdownRef.current && !passengerDropdownRef.current.contains(event.target)) {
        setShowPassengerDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter airports based on search query
  const filterAirports = (query) => {
    if (!query) return AIRPORTS;
    const lowerQuery = query.toLowerCase();
    return AIRPORTS.filter(
      (airport) =>
        airport.code.toLowerCase().includes(lowerQuery) ||
        airport.city.toLowerCase().includes(lowerQuery) ||
        airport.name.toLowerCase().includes(lowerQuery) ||
        airport.country.toLowerCase().includes(lowerQuery)
    );
  };

  const filteredFromAirports = filterAirports(fromSearchQuery);
  const filteredToAirports = filterAirports(toSearchQuery);

  const handleSelectFromAirport = (airport) => {
    setSearchData({
      ...searchData,
      from: airport.code,
      fromDisplay: `${airport.city} (${airport.code})`,
    });
    setFromSearchQuery("");
    setShowFromDropdown(false);
  };

  const handleSelectToAirport = (airport) => {
    setSearchData({
      ...searchData,
      to: airport.code,
      toDisplay: `${airport.city} (${airport.code})`,
    });
    setToSearchQuery("");
    setShowToDropdown(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    if (!searchData.from || !searchData.to) {
      alert("Please select departure and destination");
      return;
    }

    if (!searchData.departDate) {
      alert("Please select departure date");
      return;
    }

    if (searchData.tripType === "round-trip" && !searchData.returnDate) {
      alert("Please select return date");
      return;
    }

    // Prepare search data for API
    const searchPayload = {
      from: searchData.from,
      to: searchData.to,
      fromDisplay: searchData.fromDisplay,
      toDisplay: searchData.toDisplay,
      departDate: searchData.departDate,
      returnDate: searchData.returnDate,
      tripType: searchData.tripType,
      passengers: searchData.passengers,
      cabinClass: searchData.cabinClass,
    };

    console.log("Search Data:", searchPayload);

    if (onSearch) {
      onSearch(searchPayload);
    }
  };

  const updatePassengers = (type, value, e) => {
    e.stopPropagation();

    setSearchData((prev) => {
      const newPassengers = { ...prev.passengers };

      newPassengers[type] += value;

      if (type === "adults" && newPassengers[type] < 1) newPassengers[type] = 1;
      if (type !== "adults" && newPassengers[type] < 0) newPassengers[type] = 0;

      return {
        ...prev,
        passengers: newPassengers,
      };
    });
  };

  return (
    <form onSubmit={handleSearchSubmit} className="airline-flight-search-form">

      {/* Trip Type */}
      <div className="trip-type-selector">
        <label className={searchData.tripType === "round-trip" ? "active" : ""}>
          <input
            type="radio"
            name="tripType"
            value="round-trip"
            checked={searchData.tripType === "round-trip"}
            onChange={(e) =>
              setSearchData({ ...searchData, tripType: e.target.value })
            }
          />
          <span>Round Trip</span>
        </label>

        <label className={searchData.tripType === "one-way" ? "active" : ""}>
          <input
            type="radio"
            name="tripType"
            value="one-way"
            checked={searchData.tripType === "one-way"}
            onChange={(e) =>
              setSearchData({ ...searchData, tripType: e.target.value })
            }
          />
          <span>One Way</span>
        </label>

        <label className={searchData.tripType === "multi-city" ? "active" : ""}>
          <input
            type="radio"
            name="tripType"
            value="multi-city"
            checked={searchData.tripType === "multi-city"}
            onChange={(e) =>
              setSearchData({ ...searchData, tripType: e.target.value })
            }
          />
          <span>Multi City</span>
        </label>
      </div>

      {/* Main Fields */}
      <div className="search-fields-grid">

        {/* From */}
        <div className="search-field" ref={fromDropdownRef}>
          <label>From</label>
          <div className="airport-input-wrapper">
            <MapPin size={18} className="input-icon" />
            <input
              type="text"
              placeholder="Departure City or Airport"
              value={searchData.fromDisplay || fromSearchQuery}
              onChange={(e) => {
                setFromSearchQuery(e.target.value);
                setSearchData({
                  ...searchData,
                  from: "",
                  fromDisplay: "",
                });
                setShowFromDropdown(true);
              }}
              onFocus={() => setShowFromDropdown(true)}
            />
          </div>

          {showFromDropdown && (
            <div className="airport-dropdown" style={{ width: "420px", textAlign: "justify" }}>
              {filteredFromAirports.length > 0 ? (
                filteredFromAirports.map((airport) => (
                  <div
                    key={airport.code}
                    className="airport-option"
                    onClick={() => handleSelectFromAirport(airport)}
                  >
                    <div className="airport-code">{airport.code}</div>
                    <div className="airport-details">
                      <div className="airport-city">{airport.city}</div>
                      <div className="airport-name">{airport.name}</div>
                    </div>
                    <div className="airport-country">{airport.country}</div>
                  </div>
                ))
              ) : (
                <div className="no-results">No airports found</div>
              )}
            </div>
          )}
        </div>

        {/* Swap */}
        <button
          type="button"
          className="swap-btn"
          onClick={() =>
            setSearchData({
              ...searchData,
              from: searchData.to,
              to: searchData.from,
              fromDisplay: searchData.toDisplay,
              toDisplay: searchData.fromDisplay,
            })
          }
        >
          <ArrowRightLeft size={20} />
        </button>

        {/* To */}
        <div className="search-field" ref={toDropdownRef}>
          <label>To</label>
          <div className="airport-input-wrapper">
            <MapPin size={18} className="input-icon" />
            <input
              type="text"
              placeholder="Arrival City or Airport"
              value={searchData.toDisplay || toSearchQuery}
              onChange={(e) => {
                setToSearchQuery(e.target.value);
                setSearchData({
                  ...searchData,
                  to: "",
                  toDisplay: "",
                });
                setShowToDropdown(true);
              }}
              onFocus={() => setShowToDropdown(true)}
            />
          </div>

          {showToDropdown && (
            <div className="airport-dropdown" style={{ width: "420px", textAlign: "justify" }}>
              {filteredToAirports.length > 0 ? (
                filteredToAirports.map((airport) => (
                  <div
                    key={airport.code}
                    className="airport-option"
                    onClick={() => handleSelectToAirport(airport)}
                  >
                    <div className="airport-code">{airport.code}</div>
                    <div className="airport-details">
                      <div className="airport-city">{airport.city}</div>
                      <div className="airport-name">{airport.name}</div>
                    </div>
                    <div className="airport-country">{airport.country}</div>
                  </div>
                ))
              ) : (
                <div className="no-results">No airports found</div>
              )}
            </div>
          )}
        </div>

        {/* Depart */}
        <div className="search-field">
          <label>Depart</label>
          <div
            className="input-with-icon"
            onClick={() => departDateRef.current?.showPicker?.()}
          >
            <Calendar size={18} />
            <input
              ref={departDateRef}
              type="date"
              value={searchData.departDate}
              onChange={(e) =>
                setSearchData({ ...searchData, departDate: e.target.value })
              }
              min={new Date().toISOString().split("T")[0]}
              required
            />
          </div>
        </div>

        {/* Return */}
        {searchData.tripType === "round-trip" && (
          <div className="search-field">
            <label>Return</label>
            <div
              className="input-with-icon"
              onClick={() => returnDateRef.current?.showPicker?.()}
            >
              <Calendar size={18} />
              <input
                ref={returnDateRef}
                type="date"
                value={searchData.returnDate}
                onChange={(e) =>
                  setSearchData({ ...searchData, returnDate: e.target.value })
                }
                min={
                  searchData.departDate ||
                  new Date().toISOString().split("T")[0]
                }
              />
            </div>
          </div>
        )}

        {/* Passengers */}
        <div className="search-field passengers-field" ref={passengerDropdownRef}>
          <label>Passengers</label>

          <div className="passengers-dropdown">
            <div
              className="passengers-summary"
              onClick={() => setShowPassengerDropdown(!showPassengerDropdown)}
            >

              <span>
                {searchData.passengers.adults +
                  searchData.passengers.children +
                  searchData.passengers.infants}{" "}
                Passenger
              </span>
            </div>

            {showPassengerDropdown && (
              <div
                className="passengers-detail"
                onClick={(e) => e.stopPropagation()}
              >

                {["adults", "children", "infants"].map((type) => (
                  <div key={type} className="passenger-row">
                    <div className="passenger-info">
                      <span className="passenger-type">
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </span>
                    </div>

                    <div className="passenger-controls">
                      <button
                        type="button"
                        onClick={(e) => updatePassengers(type, -1, e)}
                      >
                        -
                      </button>

                      <span>{searchData.passengers[type]}</span>

                      <button
                        type="button"
                        onClick={(e) => updatePassengers(type, 1, e)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}

              </div>
            )}
          </div>
        </div>

        {/* Cabin */}
        <div className="search-field">
          <label>Cabin Class</label>

          <div className="input-with-icon">
            <Star size={18} />

            <select
              value={searchData.cabinClass}
              onChange={(e) =>
                setSearchData({ ...searchData, cabinClass: e.target.value })
              }
            >
              <option value="economy">Economy</option>
              <option value="premium-economy">Premium Economy</option>
              <option value="business">Business</option>
              <option value="first">First Class</option>
            </select>
          </div>
        </div>
      </div>

     

      {/* Search Button */}
      <button type="submit" className="search-flights-btn">
        <Search size={20} />
        Search Flights
      </button>
    </form>
  );
}

export default SearchCardNew;